import type { PostEventBodySchema } from '@mntn/api-contracts/seller/v1/events'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import type { z } from 'zod'
import { createEventClient } from './client.ts'

export type EventInput = z.input<typeof PostEventBodySchema>

export type EventsClient = {
  emitEvent: (input: EventInput) => Promise<{ ok: true }>
}

const EventClientContext = createContext<EventsClient | null>(null)

type Props = {
  enabled: boolean
  baseUrl: string
  getToken: () => Promise<string | null>
  getOrgHeaders: () => Promise<Record<string, string>> | Record<string, string>
  fetchImpl?: typeof fetch
  credentials?: RequestCredentials
  children: ReactNode
}

export const EventsProvider = ({ enabled, baseUrl, getToken, getOrgHeaders, children }: Props) => {
  const tokenRef = useRef(getToken)

  const orgRef = useRef(getOrgHeaders)

  useEffect(() => {
    tokenRef.current = getToken
  }, [getToken])

  useEffect(() => {
    orgRef.current = getOrgHeaders
  }, [getOrgHeaders])

  const value = useMemo(() => {
    const client = createEventClient({
      baseUrl,
      getToken,
      getOrgHeaders,
    })

    const emitEvent = async (event: EventInput) => {
      const result = await client.publish({ body: event })
      if (result.status === 200 || result.status === 207) {
        return { ok: true as const }
      }
      throw new Error('Failed to emit event')
    }

    return { emitEvent }
  }, [baseUrl, getOrgHeaders, getToken])

  return <EventClientContext.Provider value={enabled ? value : null}>{children}</EventClientContext.Provider>
}

/** This should remain internal. Opting for useEmitEvent for public api */
export function useEventsClient() {
  const ctx = useContext(EventClientContext)
  if (!ctx) {
    return {
      emitEvent: async (_event: EventInput) => {
        console.info('Events skipped - EventsProvider not initialized')
        return { ok: true as const }
      },
    }
  }
  return ctx
}
